import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Shield, Eye, Brain, Users, Handshake, Target,
  MessageSquare, Mail, Video, Gamepad2, Award,
  FileText, Clock, Swords, UserSearch
} from 'lucide-react';
import QuoteBuilder from './QuoteBuilder';
import { useI18n } from './i18n';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const skills = [
  { icon: Eye, text: 'Identifier les signaux faibles précurseurs de risques, menaces ou opportunités' },
  { icon: Clock, text: 'Prendre des décisions sous contrainte de temps avec un haut degré d\'incertitude' },
  { icon: MessageSquare, text: 'Stimuler la vigilance, le discernement et le partage d\'information inter-fonctions' },
  { icon: Users, text: 'Renforcer la cohésion d\'une équipe en situation de surcharge informationnelle' },
  { icon: Shield, text: 'Fiabiliser une information' },
  { icon: Brain, text: 'Maîtriser les biais cognitifs' },
  { icon: Handshake, text: 'Valoriser vos entretiens professionnels et vos compétences de négociation' },
];

const timeline = [
  { label: 'En amont', desc: 'Choix d\'un scénario en fonction des compétences techniques que vous souhaitez acquérir', icon: Target },
  { label: 'J-7', desc: 'Réception d\'un dossier de mise dans l\'ambiance et attribution des rôles de chacun', icon: FileText },
  { label: 'J-5', desc: 'Visio de présentation du jeu et de l\'application', icon: Video },
  { label: 'Jour J', desc: 'Déroulement du jeu', icon: Gamepad2 },
  { label: 'J+1', desc: 'Envoi d\'une attestation de participation et d\'un document récapitulatif des connaissances acquises', icon: Award },
];

// Stock photos
const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/247eoinp_image.png',
  team: 'https://images.unsplash.com/photo-1758518729706-b1810dd39cc6?auto=format&fit=crop&w=900&q=80',
  humint: 'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/fvonipr6_Services-Speciaux-Operation-Humint-Boite.jpg',
  boardGame: 'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/6a0brsfy_Shadow%20Corp.png',
  analysis: '/media/analysis.jpg',
};

export default function LandingHome() {
  const { t } = useI18n();
  const { basePath } = useOutletContext();
  const h = t.home;
  const skillIcons = [Eye, Clock, MessageSquare, Users, Shield, Brain, Handshake];
  const timelineIcons = [Target, FileText, Video, Gamepad2, Award];
  return (
    <>
      {/* ─── HERO / INTRODUCTION ─── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle, #34B2C8 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#34B2C8]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#F5A623]/5 rounded-full blur-[100px]" />

        <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" className="space-y-8">
              <motion.h1
                variants={fadeUp} custom={0}
                className="text-4xl sm:text-5xl font-light text-slate-900 leading-[1.15]"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {h.heroTitle}
              </motion.h1>

              <motion.div variants={fadeUp} custom={1} className="space-y-5 text-base text-slate-600 leading-relaxed">
                <p>
                  {h.heroP1} <strong className="text-slate-900">{h.heroP1Bold}</strong> {h.heroP1End}
                </p>
                <p>
                  {h.heroP2Start} <strong className="text-slate-900">{h.heroP2Bold}</strong> {h.heroP2End}
                </p>
                <p>
                  {h.heroP3Start} <strong className="text-slate-900">{h.heroP3Bold}</strong> {h.heroP3End}
                </p>
              </motion.div>

              <motion.div variants={fadeUp} custom={2} className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`${basePath}/formation`}
                  data-testid="hero-cta"
                  className="inline-flex items-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] text-white font-medium px-7 py-3 rounded-full transition-all duration-200 hover:scale-[1.03] shadow-lg shadow-[#34B2C8]/20"
                >
                  {h.heroCta}
                  <ArrowRight size={16} />
                </a>
                <Link
                  to={`${basePath}/contact`}
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#34B2C8] text-slate-700 hover:text-[#34B2C8] font-medium px-7 py-3 rounded-full transition-all duration-200"
                >
                  {h.heroCtaSecondary}
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-8 lg:mt-0"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-[#34B2C8]/10 rounded-3xl blur-2xl" />
                <video
                  controls
                  preload="none"
                  poster="/media/presentation-poster.jpg"
                  playsInline
                  className="relative w-full h-[240px] sm:h-[320px] lg:h-[420px] object-cover rounded-2xl shadow-2xl border border-slate-200/50 bg-slate-900"
                >
                  <source src="/media/presentation.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo HTML5.
                </video>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── COMMENT SE DÉROULE UN SERIOUS GAME ─── */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <img
                src={IMAGES.team}
                alt="Équipe en situation de crise"
                className="w-full h-[350px] object-cover rounded-2xl shadow-lg"
              />
            </motion.div>

            {/* Text */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              className="lg:col-span-3"
            >
              <motion.h2
                variants={fadeUp} custom={0}
                className="text-2xl sm:text-3xl font-medium text-slate-900 mb-8"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {h.gameTitle}
              </motion.h2>

              <motion.div variants={fadeUp} custom={1} className="space-y-5 text-base text-slate-600 leading-relaxed">
                <p>{h.gameP1}</p>
                <p>{h.gameP2}</p>
              </motion.div>

              <motion.div variants={fadeUp} custom={2} className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-3 px-5 py-3 bg-[#34B2C8]/5 rounded-xl border border-[#34B2C8]/15">
                  <Mail size={18} className="text-[#34B2C8] flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{h.gameBadge1}</span>
                </div>
                <div className="flex items-center gap-3 px-5 py-3 bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/15">
                  <Users size={18} className="text-[#F5A623] flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{h.gameBadge2}</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── ÉTAPES D'UNE SESSION ─── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
            <motion.h2
              variants={fadeUp} custom={0}
              className="text-2xl sm:text-3xl font-medium text-slate-900 mb-16 text-center"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {h.timelineTitle}
            </motion.h2>
          </motion.div>

          <div className="relative">
            <div className="hidden md:block absolute top-10 left-0 right-0 h-0.5 bg-slate-200" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {h.timeline.map((step, i) => {
                const Icon = timelineIcons[i];
                return (
                  <motion.div
                    key={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                    className="text-center relative"
                  >
                    <div className="relative z-10 w-20 h-20 rounded-2xl bg-white border-2 border-[#34B2C8] flex flex-col items-center justify-center mx-auto mb-4 shadow-sm">
                      <Icon size={20} className="text-[#34B2C8] mb-1" />
                      <span className="text-xs font-bold text-[#34B2C8]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {step.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed px-2">{step.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PLATEFORME SAAS ─── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
            <motion.p variants={fadeUp} custom={0} className="text-base text-slate-500 mb-3">
              {h.saasIntro}
            </motion.p>
            <motion.h2
              variants={fadeUp} custom={1}
              className="text-2xl sm:text-3xl font-medium text-slate-900 mb-12"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {h.saasTitle}
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(() => {
              const icons = [
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/></svg>,
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>,
              ];
              return h.saasItems.map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-30px' }}
                variants={fadeUp}
                custom={i * 0.15 + 2}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-[#34B2C8]/20 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-slate-700 flex items-center justify-center text-white mb-5">
                  {icons[i]}
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ));
            })()}
          </div>
        </div>
      </section>

      {/* ─── JEUX DE PLATEAU ─── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
            <motion.p
              variants={fadeUp} custom={0}
              className="text-base text-slate-600 leading-relaxed mb-12 max-w-3xl"
            >
              {h.boardIntro} <strong className="text-slate-900">{h.boardMatagot}</strong>.
            </motion.p>
          </motion.div>

          <div className="space-y-12">
            {/* HUMINT — image à droite */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={0}
              className="grid lg:grid-cols-5 gap-10 items-center"
            >
              <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:border-[#34B2C8]/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#34B2C8]/10 flex items-center justify-center">
                    <Swords size={20} className="text-[#34B2C8]" />
                  </div>
                  <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Opération HUMINT
                  </h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  {h.humintP1}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  {h.humintP2}
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  <strong className="text-slate-700">{h.humintP3}</strong> {h.humintP3End}
                </p>
              </div>
              <div className="lg:col-span-2">
                <img
                  src={IMAGES.humint}
                  alt="Boîte du jeu Services Spéciaux - Opération HUMINT"
                  className="w-full h-[320px] object-cover rounded-2xl shadow-lg"
                />
              </div>
            </motion.div>

            {/* Shadow Corps — image à gauche */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={1}
              className="grid lg:grid-cols-5 gap-10 items-center"
            >
              <div className="lg:col-span-2 order-2 lg:order-1">
                <img
                  src={IMAGES.boardGame}
                  alt="Plateau du jeu Shadow Corp"
                  className="w-full h-[320px] object-contain rounded-2xl"
                />
              </div>
              <div className="lg:col-span-3 order-1 lg:order-2 bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:border-[#F5A623]/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#F5A623]/10 flex items-center justify-center">
                    <UserSearch size={20} className="text-[#F5A623]" />
                  </div>
                  <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Shadow Corps
                  </h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  {h.shadowP1}
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {h.shadowP2Start} <strong className="text-slate-700">{h.shadowP2Bold}</strong> {h.shadowP2End}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── 7 COMPÉTENCES CLÉS ─── */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-start">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              className="lg:col-span-3"
            >
              <motion.h2
                variants={fadeUp} custom={0}
                className="text-2xl sm:text-3xl font-medium text-slate-900 mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {h.skillsTitle}
              </motion.h2>
              <motion.div variants={fadeUp} custom={0.5} className="w-16 h-1 bg-[#34B2C8] rounded-full mb-10" />

              <div className="space-y-4">
                {h.skills.map((skill, i) => {
                  const Icon = skillIcons[i];
                  return (
                    <motion.div
                      key={i}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: '-30px' }}
                      variants={fadeUp}
                      custom={i * 0.3}
                      className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#34B2C8]/20 hover:bg-white transition-all duration-300"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#34B2C8]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon size={16} className="text-[#34B2C8]" />
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{skill}</p>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 sticky top-24"
            >
              <img
                src={IMAGES.analysis}
                alt="Analyse et présentation de données"
                className="w-full h-[420px] object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── CTA FINAL ─── */}
      <section className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2
              variants={fadeUp} custom={0}
              className="text-2xl sm:text-3xl lg:text-4xl font-light text-slate-900 mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {h.ctaTitle}
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base text-slate-500 mb-10 max-w-xl mx-auto">
              {h.ctaSubtitle}
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Link
                to={`${basePath}/contact`}
                data-testid="final-cta"
                className="inline-flex items-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] text-white font-medium px-8 py-3.5 rounded-full transition-all duration-200 hover:scale-[1.03] shadow-lg shadow-[#34B2C8]/20"
              >
                {h.ctaButton}
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
